import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiPlay, FiDownload, FiTrash2, FiLoader, FiCheck, FiGlobe, FiRotateCcw } from "react-icons/fi";
import { fetchTTSVoices, generateTTS, fetchTTSHistory, deleteTTSHistory, apiRequest, errorMessage } from "../api";

const CLIENT_ID_KEY = "tts_client_id";

function getClientId() {
  try {
    let id = localStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
      id = `c_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(CLIENT_ID_KEY, id);
    }
    return id;
  } catch {
    return `c_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

const PRESET_META = {
  simple: { label: "Simple", hint: "Natural, everyday reading pace.", rate: 0, volume: 0 },
  quran: { label: "Quran Recitation", hint: "Slower, respectful, deliberate pace.", rate: -25, volume: -5 },
  song: { label: "Song Rhythm", hint: "Faster, upbeat, playful pace.", rate: 20, volume: 0 },
};

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ur", label: "Urdu" },
  { code: "hi", label: "Hindi" },
  { code: "pa", label: "Punjabi" },
  { code: "ar", label: "Arabic" },
];

const CHARACTERS = ["deep_male", "normal_male", "soft_female", "loving_female", "baby_boy", "baby_girl"];

function formatDuration(seconds) {
  if (!seconds && seconds !== 0) return "--:--";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatOffset(value) {
  return `${value > 0 ? "+" : ""}${value}%`;
}

const PAUSES = [
  { key: "short", label: "Short", hint: "About a quarter of a second between sentences." },
  { key: "natural", label: "Natural", hint: "A natural, short breath between sentences." },
  { key: "long", label: "Long", hint: "A slower, more deliberate pause between sentences." },
  { key: "original", label: "As generated", hint: "Leave the voice service's own pauses untouched." },
];

export default function TextToSpeechTool() {
  const [clientId] = useState(getClientId);
  const [voices, setVoices] = useState([]);
  const [maxLength, setMaxLength] = useState(3000);
  const [voicesError, setVoicesError] = useState("");

  const [text, setText] = useState("");
  const [language, setLanguage] = useState("en");
  const [character, setCharacter] = useState("normal_male");
  const [preset, setPreset] = useState("simple");
  const [accent, setAccent] = useState("");
  const [rate, setRate] = useState(0);
  const [pitch, setPitch] = useState(0);
  const [volume, setVolume] = useState(0);
  const [pause, setPause] = useState("natural");

  // Language of the pasted text (detected by the server) and translation into the selected language.
  const [autoTranslate, setAutoTranslate] = useState(true);
  const [detected, setDetected] = useState(null);   // { language, label, speakable } | null
  const [translating, setTranslating] = useState(false);
  const [originalText, setOriginalText] = useState(null);   // the text before "Translate now", for Undo
  const [notice, setNotice] = useState("");

  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");
  const [result, setResult] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  useEffect(() => {
    fetchTTSVoices()
      .then((data) => {
        setVoices(data.voices || []);
        setMaxLength(data.max_text_length || 3000);
      })
      .catch(() => setVoicesError("The voice engine is unavailable right now. Please try again later."));
  }, []);

  const loadHistory = useCallback(() => {
    setHistoryLoading(true);
    fetchTTSHistory(clientId)
      .then(setHistory)
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [clientId]);

  useEffect(loadHistory, [loadHistory]);

  // language -> accent -> { label, voices: character -> voice }, built from whatever the backend reports.
  const voiceMap = useMemo(() => {
    const map = {};
    voices.forEach((v) => {
      const key = CHARACTERS.find((c) => v.id.endsWith(c));
      if (!key) return;
      const byAccent = (map[v.language] = map[v.language] || {});
      const entry = (byAccent[v.accent] = byAccent[v.accent] || { label: v.accent_label, voices: {} });
      entry.voices[key] = v;
    });
    return map;
  }, [voices]);

  const availableLanguages = LANGUAGES.filter((l) => voiceMap[l.code]);
  const accents = Object.entries(voiceMap[language] || {});
  const activeAccent = voiceMap[language]?.[accent] ? accent : accents[0]?.[0] || "";
  const selectedVoice = voiceMap[language]?.[activeAccent]?.voices[character];

  // Detect the language of whatever is pasted in the editor (after a short pause in typing).
  useEffect(() => {
    const sample = text.trim();
    if (sample.length < 6) {
      setDetected(null);
      return undefined;
    }
    let alive = true;
    const timer = setTimeout(async () => {
      const { ok, data } = await apiRequest("/core/tts/detect/", { method: "POST", body: { text: sample.slice(0, 400) } });
      if (alive) setDetected(ok ? data : null);
    }, 900);
    return () => { alive = false; clearTimeout(timer); };
  }, [text]);

  const selectedLabel = LANGUAGES.find((l) => l.code === language)?.label;
  const detectedCode = detected?.language?.split("-")[0];
  const differs = !!detected && detectedCode !== language;
  const detectedVoiceLanguage = LANGUAGES.find((l) => l.code === detectedCode && voiceMap[l.code]);

  const translateNow = async () => {
    setTranslating(true);
    setGenError("");
    setNotice("");
    const { ok, data } = await apiRequest("/core/tts/translate/", { method: "POST", body: { text: text.trim(), target: language } });
    setTranslating(false);
    if (!ok) {
      setGenError(data?.detail || errorMessage(data));
      return;
    }
    setOriginalText(text);
    setText(data.text.slice(0, maxLength));
    if (data.truncated) setNotice("The translation was longer than the limit, so it was shortened at the last full sentence.");
  };

  const undoTranslate = () => {
    setText(originalText);
    setOriginalText(null);
    setNotice("");
  };

  const applyPreset = (key) => {
    setPreset(key);
    setRate(PRESET_META[key].rate);
    setVolume(PRESET_META[key].volume);
  };

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const charCount = text.length;
  const effectiveWpm = 150 * (1 + rate / 100);
  const estimatedSeconds = wordCount > 0 ? (wordCount / effectiveWpm) * 60 : 0;

  const handleGenerate = async () => {
    if (!text.trim()) {
      setGenError("Please enter some text first.");
      return;
    }
    if (!selectedVoice) {
      setGenError("Please choose a voice.");
      return;
    }
    setGenerating(true);
    setGenError("");
    setResult(null);
    try {
      const data = await generateTTS({
        text: text.trim(),
        voice_id: selectedVoice.id,
        voice_label: `${selectedVoice.name} (${voiceMap[language][activeAccent].label} ${LANGUAGES.find((l) => l.code === language)?.label})`,
        preset,
        rate,
        pitch,
        volume,
        pause,
        translate: autoTranslate,
        client_id: clientId,
      });
      if (data.status === "failed") {
        setGenError(data.error_message || "Audio generation failed.");
      } else {
        setResult(data);
        loadHistory();
      }
    } catch (err) {
      setGenError(err.message || "Failed to generate audio.");
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteHistory = async (id) => {
    if (!window.confirm("Delete this generation?")) return;
    try {
      await deleteTTSHistory(id, clientId);
      setHistory((prev) => prev.filter((h) => h.id !== id));
    } catch {
      // list re-syncs on next load
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">Text-to-Speech Generator</h2>

      {/* Full-width text editor */}
      <div className="rounded-2xl bg-white/5 border border-navy-border p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <label className="text-xs uppercase tracking-widest text-white/50 font-semibold">Your Text</label>
          <span className="text-xs text-gray-500">
            {charCount}/{maxLength} chars &middot; {wordCount} words &middot; ~{formatDuration(estimatedSeconds)}
          </span>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, maxLength))}
          placeholder="Type or paste the text you want turned into speech..."
          className="w-full min-h-[220px] px-5 py-4 rounded-xl bg-navy border border-navy-border text-white text-base leading-relaxed focus:outline-none focus:border-accent-light resize-y"
        />

        <div className="mt-4 space-y-3">
          {detected && !differs && (
            <p className="text-sm text-emerald-400 flex items-center gap-2">
              <FiGlobe /> Detected language: <b>{detected.label}</b> - it matches the selected voice language.
            </p>
          )}
          {detected && differs && (
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-100">
              <p className="flex items-center gap-2">
                <FiGlobe className="shrink-0" /> Detected language: <b>{detected.label}</b>. The selected voice speaks <b>{selectedLabel}</b>.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={translateNow}
                  disabled={translating}
                  className="px-4 py-1.5 rounded-full bg-amber-400 text-navy font-semibold text-xs disabled:opacity-60 inline-flex items-center gap-1.5"
                >
                  {translating ? <><FiLoader className="animate-spin" /> Translating...</> : <>Translate to {selectedLabel} now</>}
                </button>
                {detectedVoiceLanguage && (
                  <button
                    type="button"
                    onClick={() => { setLanguage(detectedVoiceLanguage.code); setAccent(""); }}
                    className="px-4 py-1.5 rounded-full border border-amber-400/60 text-amber-100 font-semibold text-xs hover:bg-amber-400/10"
                  >
                    Use the {detectedVoiceLanguage.label} voice instead
                  </button>
                )}
              </div>
            </div>
          )}
          {originalText !== null && (
            <button type="button" onClick={undoTranslate} className="text-xs text-gray-400 hover:text-accent-light inline-flex items-center gap-1.5">
              <FiRotateCcw /> Undo translation (back to the original text)
            </button>
          )}
          {notice && <p className="text-xs text-amber-300">{notice}</p>}
          <label className="flex items-start gap-2.5 text-sm text-gray-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoTranslate}
              onChange={(e) => setAutoTranslate(e.target.checked)}
              className="mt-0.5 accent-accent-light"
            />
            <span>
              Translate to the selected language automatically when I generate audio
              <span className="block text-xs text-gray-500">Text that is already in {selectedLabel} is left as it is.</span>
            </span>
          </label>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Style preset */}
        <div className="rounded-2xl bg-white/5 border border-navy-border p-6">
          <label className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4 block">Style</label>
          <div className="flex flex-wrap gap-3">
            {Object.entries(PRESET_META).map(([key, meta]) => (
              <button
                key={key}
                type="button"
                onClick={() => applyPreset(key)}
                title={meta.hint}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  preset === key
                    ? "bg-brand-gradient text-white shadow-md shadow-accent/20"
                    : "bg-white/5 border border-navy-border text-gray-200 hover:border-accent-light hover:text-accent-light"
                }`}
              >
                {meta.label}
              </button>
            ))}
          </div>
          <p className="text-sm text-gray-400 mt-4">{PRESET_META[preset]?.hint}</p>
        </div>

        {/* Fine-tune */}
        <div className="rounded-2xl bg-white/5 border border-navy-border p-6">
          <div className="flex items-center justify-between mb-4">
            <label className="text-xs uppercase tracking-widest text-white/50 font-semibold">Fine-Tune</label>
            <button
              type="button"
              onClick={() => { setRate(0); setPitch(0); setVolume(0); setPause("natural"); setPreset("simple"); }}
              className="text-xs text-gray-400 hover:text-accent-light"
            >
              Reset
            </button>
          </div>
          <div className="space-y-5">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-300">Speed</span>
                <span className="text-accent-light font-medium">{(1 + rate / 100).toFixed(2)}&times; ({formatOffset(rate)})</span>
              </div>
              <input
                type="range" min="-50" max="100" step="5"
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                className="w-full accent-accent-light"
              />
              <div className="flex justify-between text-[10px] text-gray-500"><span>Slower</span><span>Faster</span></div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-300">Pitch</span>
                <span className="text-accent-light font-medium">{pitch > 0 ? "+" : ""}{pitch} Hz</span>
              </div>
              <input
                type="range" min="-50" max="50" step="5"
                value={pitch}
                onChange={(e) => setPitch(Number(e.target.value))}
                className="w-full accent-accent-light"
              />
              <div className="flex justify-between text-[10px] text-gray-500"><span>Deeper</span><span>Higher</span></div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-300">Volume</span>
                <span className="text-accent-light font-medium">{formatOffset(volume)}</span>
              </div>
              <input
                type="range" min="-50" max="50" step="5"
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                className="w-full accent-accent-light"
              />
            </div>
            <div>
              <span className="text-sm text-gray-300 block mb-2">Pause between sentences</span>
              <div className="flex flex-wrap gap-2">
                {PAUSES.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    title={p.hint}
                    onClick={() => setPause(p.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                      pause === p.key ? "bg-accent-light text-navy" : "bg-white/5 border border-navy-border text-gray-300 hover:border-accent-light"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Language + voice picker */}
      <div className="rounded-2xl bg-white/5 border border-navy-border p-6 mb-6">
        <label className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4 block">Language</label>
        {voicesError ? (
          <p className="text-sm text-red-400">{voicesError}</p>
        ) : availableLanguages.length === 0 ? (
          <p className="text-sm text-gray-400">Loading voices...</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-6">
              {availableLanguages.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => { setLanguage(l.code); setAccent(""); }}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                    language === l.code
                      ? "bg-accent-light text-navy"
                      : "bg-white/5 border border-navy-border text-gray-300 hover:border-accent-light"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            {language === "pa" ? (
              <p className="text-xs text-gray-400 mb-6">
                Punjabi has no voice of its own. Text in Urdu script (Shahmukhi) is read with the natural Pakistani Urdu voices;
                text in Gurmukhi is converted and read with the Hindi voices, so expect a Hindi-leaning accent.
              </p>
            ) : accents.length > 1 && (
              <>
                <label className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3 block">Accent</label>
                <div className="flex flex-wrap gap-2 mb-6">
                  {accents.map(([code, entry]) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setAccent(code)}
                      className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                        activeAccent === code
                          ? "bg-brand-gradient text-white shadow-md shadow-accent/20"
                          : "bg-white/5 border border-navy-border text-gray-300 hover:border-accent-light"
                      }`}
                    >
                      {entry.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            <label className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3 block">Voice</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {CHARACTERS.map((key) => {
                const v = voiceMap[language]?.[activeAccent]?.voices[key];
                if (!v) return null;
                const active = character === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setCharacter(key)}
                    className={`relative flex flex-col items-start gap-1 px-4 py-3 rounded-xl text-left transition-colors ${
                      active
                        ? "bg-accent/15 border border-accent-light text-white"
                        : "bg-navy border border-navy-border text-gray-300 hover:border-accent-light/60"
                    }`}
                  >
                    {active && <FiCheck className="absolute top-3 right-3 text-accent-light" size={16} />}
                    <span className="font-semibold text-sm">{v.name}</span>
                    <span className="text-xs text-gray-500 capitalize">{v.gender}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {genError && (
        <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mb-6">{genError}</p>
      )}

      <motion.button
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        onClick={handleGenerate}
        disabled={generating || !selectedVoice}
        className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold text-base shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {generating ? (
          <>
            <FiLoader className="animate-spin" /> Generating...
          </>
        ) : (
          <>
            <FiPlay /> Generate Audio
          </>
        )}
      </motion.button>

      <AnimatePresence>
        {result && result.audio_file && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6"
          >
            <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">Result</p>
            <audio controls src={result.audio_file} className="w-full mb-4" />
            {result.translated && (
              <div className="mb-4 rounded-xl bg-navy border border-navy-border px-4 py-3">
                <p className="text-xs text-gray-400 mb-1">
                  Translated from {result.detected_label || "the original language"} to {selectedLabel} before reading:
                </p>
                <p className="text-sm text-gray-200 whitespace-pre-line">{result.spoken_text}</p>
                {result.truncated && <p className="text-xs text-amber-300 mt-2">The translation was shortened to fit the length limit.</p>}
              </div>
            )}
            <a
              href={result.audio_file}
              download
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 hover:border-accent-light text-white text-sm font-medium transition-colors"
            >
              <FiDownload /> Download Audio
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Generation history */}
      <div className="mt-10">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your Generations</p>
        {historyLoading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing generated yet. Your history will appear here.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {history.map((h) => (
              <div key={h.id} className="rounded-xl bg-white/5 border border-navy-border p-4">
                <p className="text-sm text-gray-200 line-clamp-2 mb-2">{h.text}</p>
                <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                  <span>{h.voice_label || PRESET_META[h.preset]?.label} &middot; {formatDuration(h.duration_seconds)}</span>
                  <span>{h.status === "failed" ? "Failed" : "Ready"}</span>
                </div>
                {h.status === "completed" && h.audio_file && (
                  <div className="flex items-center gap-3">
                    <audio controls src={h.audio_file} className="w-full h-8" />
                    <a href={h.audio_file} download title="Download" className="text-gray-400 hover:text-accent-light shrink-0">
                      <FiDownload size={16} />
                    </a>
                    <button onClick={() => handleDeleteHistory(h.id)} title="Delete" className="text-gray-400 hover:text-red-400 shrink-0">
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
